import { Anchor, Box, Group, Progress, Stack, Text } from '@mantine/core';
// @ts-expect-error
import { nice, ticks } from 'd3-array';
import { i18nKeys, useTranslation } from 'locales';
import { Link } from 'react-router-dom';
import type { AgedBalanceCompact } from 'shared/hooks/use-load-aged-balance';
import { useAgedBalanceColors } from 'shared/hooks/utils/use-aged-balance-colors';
import {
  useLocalizedCurrencyFormatter,
  useSafeLocalizedCompactCurrencyFormatter,
} from 'shared/utils/currency';

import { formatPercentage } from './percentage';

const OVERDUE_BUCKETS = ['0', '30', '60', '90'] as const;

type AgedBalanceBarsProps = {
  agedBalanceCompact: Pick<AgedBalanceCompact, 'credits' | 'debits'>;
  links?: Record<(typeof OVERDUE_BUCKETS)[number] | 'notDue', string>;
};

export const AgedBalanceBars = ({ agedBalanceCompact, links }: AgedBalanceBarsProps) => {
  const { t } = useTranslation();
  const formatter = useLocalizedCurrencyFormatter();
  const compactFormatter = useSafeLocalizedCompactCurrencyFormatter();
  const colors = useAgedBalanceColors();
  const { debits, credits } = agedBalanceCompact;

  const buckets = OVERDUE_BUCKETS.map(bucket => ({
    bucket,
    amount: debits[bucket] - credits[bucket],
  }));
  const overdue = buckets.reduce((sum, { amount }) => sum + amount, 0);
  const largest = Math.max(...buckets.map(({ amount }) => amount));
  const [, scaleEnd]: [number, number] = nice(0, largest, 2);
  const scale: Array<number> = ticks(0, scaleEnd, 2);

  const notDueBox = (
    <Group
      justify="space-between"
      px="md"
      py="sm"
      bg="purple.0"
      style={{ borderRadius: 12, border: '1px dashed var(--mantine-color-purple-3)' }}
    >
      <Text size="sm" fw={600} c="purple.6">
        {t(i18nKeys.ANALYTICS.AGED_BALANCE.notDue)}
      </Text>
      <Text size="sm" fw={600}>
        {formatter.format(debits.notDue - credits.notDue).replaceAll(' ', ' ')}
      </Text>
    </Group>
  );

  return (
    <Stack gap="md">
      <Stack component="ul" gap="sm" p={0} m={0} style={{ listStyle: 'none' }}>
        {buckets.map(({ bucket, amount }) => {
          const label = t(i18nKeys.ANALYTICS.AGED_BALANCE[bucket]);
          const bar = (
            <Progress.Root size={26} radius={7} bg="purple.0" style={{ flex: 1 }}>
              <Progress.Section
                value={scaleEnd === 0 ? 0 : (amount / scaleEnd) * 100}
                color={colors[bucket]}
                withAria={false}
                aria-label={label}
              />
            </Progress.Root>
          );

          return (
            <Group component="li" key={bucket} wrap="nowrap">
              <Text size="sm" fw={500} w={96}>
                {label}
              </Text>
              {links ? (
                <Box component={Link} to={links[bucket]} style={{ flex: 1 }}>
                  {bar}
                </Box>
              ) : (
                bar
              )}
              <Text size="sm" w={138} ta="right">
                <Text span inherit fw={600} c="gray.10">
                  {formatter.format(amount)}
                </Text>
                {overdue !== 0 && (
                  <Text span inherit c="dimmed">
                    {` · ${formatPercentage(amount / overdue)}`}
                  </Text>
                )}
              </Text>
            </Group>
          );
        })}
      </Stack>
      <Group wrap="nowrap">
        <Box w={96} />
        <Group
          component="ul"
          aria-label={t(i18nKeys.ANALYTICS.AGED_BALANCE.SCALE)}
          justify="space-between"
          p={0}
          m={0}
          style={{ flex: 1, listStyle: 'none' }}
        >
          {scale.map(tick => (
            <Text component="li" key={tick} size="xs" c="dimmed">
              {compactFormatter.format(tick)}
            </Text>
          ))}
        </Group>
        <Box w={138} />
      </Group>
      {links ? (
        <Anchor component={Link} to={links.notDue} underline="never" c="inherit" display="block">
          {notDueBox}
        </Anchor>
      ) : (
        notDueBox
      )}
    </Stack>
  );
};
